import React, { useState, useMemo } from "react";
import {
  Select,
  Input,
  Table,
  Modal,
  notification,
  Dropdown,
} from "antd";
import { LuFilter } from "react-icons/lu";
import {
  CloseOutlined,
  SearchOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  useGetCampaignsQuery,
  useDeleteCampaignMutation,
  useGetOrganizationsQuery,
  useGetCampaignStatsQuery,
} from "../../store/api/apiSlice";
import CreateCampaignModal from "../Modals/CreateCampaignModal";
import { LayoutGrid, List } from "lucide-react";
import { useSelector } from "react-redux";
import { selectCurrentRole } from "../../store/auth/authSlice";
import { ACTIONS_DROPDOWN_PROPS } from "../../constants/actionsDropdown";
import { getStatusColor, getStatusDisplayText } from "../../utils";
import {
  getCampaignSortTime,
  withDerivedCampaignStatuses,
} from "../../utils/campaigns";
const Campaigns = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [formTypeFilter, setFormTypeFilter] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [viewType, setViewType] = useState(isMobile ? "grid" : "list");
  const [organizationFilter, setOrganizationFilter] = useState(null);

  // Fetch data
  const {
    data: campaignsData,
    isLoading: isLoadingCampaigns,
    refetch: refetchCampaigns,
  } = useGetCampaignsQuery();
  const { data: organizationsData } = useGetOrganizationsQuery();
  const { data: statsData } = useGetCampaignStatsQuery();
  const [deleteCampaign] = useDeleteCampaignMutation();
  const userRole = useSelector(selectCurrentRole);
  
  const showDeleteConfirm = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this campaign?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteCampaign(record._id).unwrap();
          notification.success({
            message: "Success",
            description: "Campaign deleted successfully",
          });
          refetchCampaigns();
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.message || "Failed to delete campaign",
          });
        }
      },
    });
  };

  const handleEdit = (record) => {
    setEditingCampaign(record);
    setIsModalVisible(true);
  };

  const getActionsMenuItems = () => [
    { key: "edit", label: "Edit", icon: <EditOutlined /> },
    {
      key: "delete",
      label: "Delete",
      danger: true,
      icon: <DeleteOutlined />,
    },
  ];

  const handleActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "edit":
        handleEdit(record);
        break;
      case "delete":
        showDeleteConfirm(record);
        break;
      default:
        break;
    }
  };

  const renderActionsDropdown = (record) => (
    <div
      className="flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Dropdown
        {...ACTIONS_DROPDOWN_PROPS}
        menu={{
          items: getActionsMenuItems(),
          onClick: (event) => handleActionMenuClick(record, event.key, event),
        }}
      >
        <button
          type="button"
          className="actions-dots-btn"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Open actions for ${record?.title || "campaign"}`}
        >
          <MoreOutlined />
        </button>
      </Dropdown>
    </div>
  );

  // Use useMemo to filter and sort the campaigns data
  const filteredData = useMemo(() => {
    const campaigns = withDerivedCampaignStatuses(campaignsData?.data);

    let filtered = campaigns.filter((item) => {
      const searchLower = searchText?.toLowerCase().trim();
      const matchesSearch =
        !searchText ||
        item.title.toLowerCase().includes(searchLower) ||
        (item.description &&
          item.description.toLowerCase().includes(searchLower));

      const matchesStatus = !statusFilter || item.status === statusFilter;

      const matchesFormType =
        !formTypeFilter ||
        (item.forms && item.forms.some((form) => form.type === formTypeFilter));

      const matchesOrganization =
        !organizationFilter ||
        item.organization?._id === organizationFilter ||
        (item.organizations &&
          item.organizations.some((org) => org._id === organizationFilter));

      return (
        matchesSearch && matchesStatus && matchesFormType && matchesOrganization
      );
    });

    if (dateRange === "newest") {
      filtered.sort((a, b) => getCampaignSortTime(b) - getCampaignSortTime(a));
    } else if (dateRange === "oldest") {
      filtered.sort((a, b) => getCampaignSortTime(a) - getCampaignSortTime(b));
    }

    return filtered;
  }, [
    campaignsData,
    searchText,
    statusFilter,
    formTypeFilter,
    dateRange,
    organizationFilter,
  ]);
  const GridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredData?.map((campaign) => (
        <div
          key={campaign._id}
          className="bg-white p-4 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow"
          onClick={() =>
            navigate(
              `/${window.location.pathname.split("/")[1]}/campaigns/${
                campaign._id
              }`,
            )
          }
        >
          <div className="flex justify-between items-start mb-4 flex-wrap">
            <div>
              <h3 className="font-medium text-gray-900 my-0 truncate">
                {campaign.title}
              </h3>
              <p className="text-sm text-gray-500 line-clamp-2">
                {campaign.description}
              </p>
            </div>
            <div
              className={`px-2 text-center capitalize py-1 rounded-full text-sm ${getStatusColor(
                campaign.status,
              )}`}
            >
              {getStatusDisplayText(campaign.status)}
            </div>
          </div>

          {userRole === "admin" && (
            <div className="flex justify-end items-center">
              {renderActionsDropdown(campaign)}
            </div>
          )}
        </div>      ))}
    </div>
  );

  const columns = [
    {
      title: "Sr#",
      dataIndex: "srNo",
      width: 70,
      onHeaderCell: () => ({ className: "campaigns-header-cell" }),
      render: (_, __, index) => index + 1,
    },
    {
      title: "Campaign",
      key: "campaign",
      onHeaderCell: () => ({ className: "campaigns-header-cell" }),
      render: (_, record) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate">{record.title}</div>
          <div className="text-sm text-gray-500 truncate">
            {record.description}
          </div>
        </div>
      ),
    },
    {
      title: "Organisation",
      key: "organisations",
      width: 130,
      onHeaderCell: () => ({ className: "campaigns-header-cell" }),
      onCell: () => ({ className: "campaigns-org-cell" }),
      render: (_, record) => (
        <div className="text-gray-900 text-sm truncate">
          {record.organization?.name || "N/A"}
        </div>
      ),
    },
    {
      title: "Forms",
      dataIndex: "forms",
      width: 80,
      align: "center",
      onHeaderCell: () => ({ className: "campaigns-header-cell" }),
      render: (forms) => forms?.length || 0,
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      align: "center",
      onHeaderCell: () => ({ className: "campaigns-header-cell" }),
      render: (_, record) => (
        <span
          className={`inline-flex px-2 py-1 capitalize rounded-full text-xs whitespace-nowrap ${getStatusColor(
            record.status,
          )}`}
        >
          {getStatusDisplayText(record.status)}
        </span>
      ),
    },
  ];

  const actionsColumn = {
    title: "",
    key: "actions",
    width: 56,
    align: "center",
    onHeaderCell: () => ({
      className: "campaigns-actions-cell",
      style: { width: 56, minWidth: 56, maxWidth: 56 },
    }),
    onCell: () => ({
      className: "campaigns-actions-cell",
      style: { width: 56, minWidth: 56, maxWidth: 56, padding: "8px 4px" },
    }),
    render: (_, record) => renderActionsDropdown(record),
  };

  const StatCard = ({ value, label }) => (
    <div className="bg-[#F9FAFB] rounded-xl p-6">
      <div className="space-y-1">
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
    </div>
  );

  const handleSuccess = () => {
    refetchCampaigns();
    setIsModalVisible(false);
    setEditingCampaign(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-0 selectSecondary">
          <h3 className="text-md font-semibold m-0">Overview</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          <StatCard
            value={statsData?.data?.total || 0}
            label="Total Campaigns"
          />
          <StatCard
            value={
              withDerivedCampaignStatuses(campaignsData?.data).filter(
                (campaign) => campaign.status === "active",
              ).length
            }
            label="Active Campaign"
          />
          <StatCard
            value={statsData?.data?.totalResponses || 0}
            label="Total Responses"
          />
          <StatCard
            value={`${Number(statsData?.data?.responseRate || 0).toFixed(1)}%`}
            label="Response Rate"
          />
        </div>
      </div>
      {/* <RiContactsBook3Line /> */}
      {/* Desktop Layout */}
      <div className="flex flex-wrap items-center gap-4  ">
        <div className="flex bg-secondary rounded-xl p-1.5">
          <button
            className={`view-toggle-btn ${
              viewType === "list" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("list")}
          >
            <List size={20} />
          </button>
          <button
            className={`view-toggle-btn ${
              viewType === "grid" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("grid")}
          >
            <LayoutGrid size={20} />
          </button>
        </div>
        <div className="relative flex-1 md:min-w-[200px]">
          <Input
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined className="text-gray-400" />}
            suffix={
              searchText ? (
                <CloseOutlined
                  className="text-gray-400 cursor-pointer hover:text-gray-600"
                  onClick={() => setSearchText("")}
                />
              ) : null
            }
            className="h-11 !rounded-xl bg-white w-full"
          />
        </div>
        {userRole === "admin" && (
          <Select
            value={organizationFilter}
            onChange={setOrganizationFilter}
            className="h-11 !rounded-xl w-[47%] sm:w-40"
            placeholder="Organisation"
            allowClear
            options={organizationsData?.data?.map((org) => ({
              value: org._id,
              label: org.name,
            }))}
          />
        )}
        <Select
          placeholder="Sort By"
          value={dateRange}
          onChange={setDateRange}
          className="h-11 !rounded-xl w-[47%] sm:w-40"
          allowClear
          options={[
            { value: "newest", label: "Newest" },
            { value: "oldest", label: "Oldest" },
          ]}
        />

        <Select
          allowClear
          value={statusFilter}
          onChange={setStatusFilter}
          className="h-11 !rounded-xl w-full sm:w-40"
          placeholder="Status"
          options={[
            { value: "active", label: "Active" },
            { value: "complete", label: "Completed" },
            { value: "draft", label: "Draft" },
            { value: "not_active", label: "Ended" },
          ]}
        />

        <Select
          placeholder="Form Type"
          className="w-[77%] md:w-40 rounded-xl h-12"
          value={formTypeFilter}
          onChange={setFormTypeFilter}
          allowClear
        >
          <Select.Option value="survey">Survey</Select.Option>
          <Select.Option value="form">Form</Select.Option>
          <Select.Option value="complaint">Complaint</Select.Option>
        </Select>

        <button className="h-11 w-11 flex items-center justify-center rounded-xl bg-secondary border border-red-500">
          <LuFilter size={20} className="text-red-500" />
        </button>

        {/* <Button
          onClick={() => {
            setEditingCampaign(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
          className="h-11 ml-auto bg-primary text-white rounded-xl font-medium flex items-center justify-center gap-2"
        >
          <CgAddR className="text-lg" /> Create
        </Button> */}
      </div>

      {viewType === "list" ? (
        <Table
          columns={
            userRole === "admin" ? [...columns, actionsColumn] : columns
          }
          dataSource={filteredData}
          pagination={false}
          loading={isLoadingCampaigns}
          onRow={(record) => ({
            onClick: () =>
              navigate(
                `/${window.location.pathname.split("/")[1]}/campaigns/${
                  record._id
                }`,
              ),
          })}
          className="campaigns-table"
          tableLayout="fixed"
        />
      ) : (
        <GridView />
      )}

      <CreateCampaignModal
        visible={isModalVisible}
        onClose={() => {
          setIsModalVisible(false);
          setEditingCampaign(null);
        }}
        editData={editingCampaign}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default Campaigns;
